import { ClientResponseError, type RecordModel } from 'pocketbase'
import { pb } from '@/libs/pocketbase'
import type { CompanyRole, DeptRole } from '@/features/permissions/domain/permission'
import type { AuthPort, AuthResult, Credentials, User } from '../domain/auth'
import type { TeamMembershipPort } from '../domain/team-membership'
import { pbTeamMembershipAdapter } from './pb-team-membership'

// teamIds start empty here: membership lives in the team_members collection, not
// on the user record. Resolve it with enrichTeamIds before the user reaches a
// session, so team-scoped views (e.g. workflow phases) have the right scope.
const recordToUser = (record: RecordModel): User => {
	const avatarFilename = record['avatar'] as string | undefined
	return {
		id: record.id,
		email: record['email'] as string,
		fullName: (record['name'] as string) ?? null,
		avatarUrl: avatarFilename ? pb.files.getURL(record, avatarFilename) : null,
		onboarded: (record['onboarded'] as boolean) ?? false,
		companyRole: ((record['company_role'] as string) || 'employee') as CompanyRole,
		deptRole: ((record['dept_role'] as string) || null) as DeptRole,
		departmentId: (record['department_id'] as string) || null,
		teamIds: [],
	}
}

const enrichTeamIds = async (user: User, port: TeamMembershipPort = pbTeamMembershipAdapter): Promise<User> => ({
	...user,
	teamIds: await port.listTeamIdsForUser(user.id),
})

const httpAuthAdapter: AuthPort = {
	async login(credentials: Credentials): Promise<AuthResult> {
		try {
			const auth = await pb.collection('users').authWithPassword(credentials.email, credentials.password)
			return { success: true, session: { user: await enrichTeamIds(recordToUser(auth.record)) } }
		} catch (err) {
			if (err instanceof ClientResponseError) {
				const message = err.status === 400 ? 'Invalid email or password' : err.message
				return { success: false, error: { message } }
			}
			return { success: false, error: { message: 'Login failed. Please try again.' } }
		}
	},

	async logout(): Promise<void> {
		pb.authStore.clear()
	},
}

export { httpAuthAdapter, recordToUser, enrichTeamIds }
